import { generatePath } from 'react-router-dom';
import URL from '../../../../constants/api-constants';
import http from '../../../../store/http/http-common';
import { IChannelData } from '../../../../types/channel-types';
import { ChangeChannelAction, CycleSettingAction } from '../../../../enums/action';
import { toast } from 'react-toastify';
import { ICycleSettingsData } from '../../../../types/cycle-settings-types';
import IconPlay from '../../../../components/Icons/IconPlay/IconPlay';
import { FC } from 'react';
import { Icon } from '../../../../types/icon';
import IconPaused from '../../../../components/Icons/IconPaused/IconPaused';
import IconCrossPaused from '../../../../components/Icons/IconCrossPaused/IconCrossPaused';
import IconStop from '../../../../components/Icons/IconStop/IconStop';
import { number, object } from 'yup';
import i18n from '../../../../infrastructure/i18n';
import ValidationAction from '../../../../types/validation-action';

export const changeChannelAction = async (
  controller_id: string,
  channel_num: number,
  action: ChangeChannelAction,
  newValue: string,
  manualFaultsOnly?: boolean,
) => {
  try {
    const payload: any = { action, value: newValue };
    if (manualFaultsOnly !== undefined) {
      payload.manualFaultsOnly = manualFaultsOnly;
    }
    await http.put(
      generatePath(URL.CHANNEL, {
        id: controller_id,
        channel_id: channel_num,
      }),
      payload,
    );
    toast.success('Action successfully executed');
  } catch (error) {
    toast.error(error.message);
  }
};
export const changeChannelActionWithReturn = async (
  controller_id: string,
  channel_num: number,
  action: ChangeChannelAction,
  newValue: string,
  manualFaultsOnly?: boolean,
) => {
  try {
    const payload: any = { action, value: newValue };
    if (manualFaultsOnly !== undefined) {
      payload.manualFaultsOnly = manualFaultsOnly;
    }
    await http.put(
      generatePath(URL.CHANNEL, {
        id: controller_id,
        channel_id: channel_num,
      }),
      payload,
    );
    toast.success('Action successfully executed');
    return true
  } catch (error) {
    toast.error(error.message);
    return false
  }
};

export const applyCurrentScalingToAllChannels = async (
  controller_id: string,
  newValue: string,
): Promise<boolean> => {
  try {
    await http.put(
      generatePath(URL.CHANNEL_CURRENT_SCALING_APPLY_ALL, {
        id: controller_id,
      }),
      { action: ChangeChannelAction.SET_CHANNEL_CURRENT_SCALING, value: newValue },
    );
    toast.success('Current scaling applied to all channels');
    return true;
  } catch (error) {
    toast.error(error.message);
    return false;
  }
};

// Same as changeChannelActionWithReturn, but without a per-call toast — for bulk
// operations (e.g. saving many channel names at once) that show one summary toast instead.
export const changeChannelActionSilent = async (
  controller_id: string,
  channel_num: number,
  action: ChangeChannelAction,
  newValue: string,
): Promise<boolean> => {
  try {
    await http.put(
      generatePath(URL.CHANNEL, {
        id: controller_id,
        channel_id: channel_num,
      }),
      { action, value: newValue },
    );
    return true;
  } catch {
    return false;
  }
};
type Action = {
  inputType: React.InputHTMLAttributes<HTMLInputElement>['type'];
  labelText: string;
};

export enum ChannelTcAction {
  ASSIGN = 'ASSIGN',
  REMOVE = 'REMOVE',
}

export type ChangeChannelActionType = ValidationAction & {
  actionName: ChangeChannelAction;
  actionField: keyof Omit<IChannelData, 'units' | 'isActive' | 'id' | 'channel'>;
  suggestion?: string;
  transformValue?: (value: string | number) => string | number;
  //TODO
  //ADD validation schema
} & Action;

export type ChangeCycleSettingsActionType = {
  actionName: CycleSettingAction;
  actionField: keyof ICycleSettingsData;
  //TODO
  //ADD validation schema
} & Action;

export type ButtonActions = Omit<ChangeChannelActionType, 'inputType' | 'actionField'>;

export const setChannelNameAction: ChangeChannelActionType = {
  inputType: 'text',
  actionName: ChangeChannelAction.SET_CHANNEL_NAME,
  actionField: 'name',
  labelText: 'Name',
};

export const stopChannelAction: Omit<ChangeChannelActionType, 'inputType'> & { Icon: FC<Icon> } = {
  actionName: ChangeChannelAction.STOP_CHANNEL,
  actionField: 'state',
  labelText: 'State',
  Icon: IconStop,
};

export const startChannelAction: Omit<ChangeChannelActionType, 'inputType'> & { Icon: FC<Icon> } = {
  actionName: ChangeChannelAction.START_CHANNEL,
  actionField: 'state',
  labelText: 'State',
  Icon: IconPlay,
};

export const pauseChannelAction: Omit<ChangeChannelActionType, 'inputType'> & { Icon: FC<Icon> } = {
  actionName: ChangeChannelAction.PAUSE_CHANNEL,
  actionField: 'state',
  labelText: 'State',
  Icon: IconPaused,
};

export const unpauseChannelAction: Omit<ChangeChannelActionType, 'inputType'> & { Icon: FC<Icon> } = {
  actionName: ChangeChannelAction.UNPAUSE_CHANNEL,
  actionField: 'state',
  labelText: 'State',
  Icon: IconCrossPaused,
};

export const winampActions = [startChannelAction, pauseChannelAction, unpauseChannelAction, stopChannelAction];

export const CHANNEL_SETTINGS_LIMITS = {
  SHUNT_NUMBER: { MIN: 1, MAX: 4 },
  MACHINE_NUMBER: { MIN: 1, MAX: 4 },
  PHASE_NUMBER: { MIN: 1, MAX: 3 },
  CURRENT_SCALING: { MIN: 0, MAX: 500 },
  MAXIMUM_CHANNEL_TEMPERATURE: { MIN: 0, MAX: 2400 },
  TRIM_CHANNEL_SETPOINT: { MIN: -1500, MAX: 1500 },
  POWER_OUTPUT_SCALE: { MIN: 0, MAX: 100 },
};

export const channelActions: ChangeChannelActionType[] = [
  setChannelNameAction,
  {
    inputType: 'number',
    actionName: ChangeChannelAction.SET_CHANNEL_SHUNT_NUMBER,
    actionField: 'shunt_number',
    labelText: i18n.t('channels.settings.shunt_number'),
    validationSchema: object({
      shunt_number: number()
        .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
        .min(
          CHANNEL_SETTINGS_LIMITS.SHUNT_NUMBER.MIN,
          i18n.t('errors.numberValue.min', '', { minNum: CHANNEL_SETTINGS_LIMITS.SHUNT_NUMBER.MIN }),
        )
        .max(
          CHANNEL_SETTINGS_LIMITS.SHUNT_NUMBER.MAX,
          i18n.t('errors.numberValue.max', '', { maxNum: CHANNEL_SETTINGS_LIMITS.SHUNT_NUMBER.MAX }),
        )
        .required(i18n.t('errors.requiredValue')),
    }),
  },
  {
    inputType: 'number',
    actionName: ChangeChannelAction.SET_CHANNEL_MACHINE_NUMBER,
    actionField: 'machine_number',
    labelText: i18n.t('channels.settings.machine_number'),
    validationSchema: object({
      machine_number: number()
        .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
        .min(
          CHANNEL_SETTINGS_LIMITS.MACHINE_NUMBER.MIN,
          i18n.t('errors.numberValue.min', '', { minNum: CHANNEL_SETTINGS_LIMITS.MACHINE_NUMBER.MIN }),
        )
        .max(
          CHANNEL_SETTINGS_LIMITS.MACHINE_NUMBER.MAX,
          i18n.t('errors.numberValue.max', '', { maxNum: CHANNEL_SETTINGS_LIMITS.MACHINE_NUMBER.MAX }),
        )
        .required(i18n.t('errors.requiredValue')),
    }),
  },
  {
    inputType: 'number',
    actionName: ChangeChannelAction.SET_CHANNEL_PHASE_NUMBER,
    actionField: 'phase_number',
    labelText: i18n.t('channels.settings.phase_number'),
    validationSchema: object({
      phase_number: number()
        .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
        .min(
          CHANNEL_SETTINGS_LIMITS.PHASE_NUMBER.MIN,
          i18n.t('errors.numberValue.min', '', { minNum: CHANNEL_SETTINGS_LIMITS.PHASE_NUMBER.MIN }),
        )
        .max(
          CHANNEL_SETTINGS_LIMITS.PHASE_NUMBER.MAX,
          i18n.t('errors.numberValue.max', '', { maxNum: CHANNEL_SETTINGS_LIMITS.PHASE_NUMBER.MAX }),
        )
        .required(i18n.t('errors.requiredValue')),
    }),
  },
  {
    inputType: 'number',
    actionName: ChangeChannelAction.SET_CHANNEL_CURRENT_SCALING,
    actionField: 'current_scaling',
    labelText: i18n.t('channels.settings.current_scaling'),
    transformValue: (value) => Number(value) * 10,
    validationSchema: object({
      current_scaling: number()
        .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
        .required(i18n.t('errors.requiredValue'))
        .test(
          'min-after-multiply',
          i18n.t('errors.numberValue.min', '', { minNum: CHANNEL_SETTINGS_LIMITS.CURRENT_SCALING.MIN }),
          (value) => {
            if (value === undefined || value === null) return false;
            return value * 10 >= CHANNEL_SETTINGS_LIMITS.CURRENT_SCALING.MIN;
          },
        )
        .test(
          'max-after-multiply',
          i18n.t('errors.numberValue.max', '', { maxNum: CHANNEL_SETTINGS_LIMITS.CURRENT_SCALING.MAX }),
          (value) => {
            if (value === undefined || value === null) return false;
            return value * 10 <= CHANNEL_SETTINGS_LIMITS.CURRENT_SCALING.MAX;
          },
        ),
    }),
  },
  {
    inputType: 'number',
    actionName: ChangeChannelAction.SET_CHANNEL_MAXIMUM_CHANNEL_TEMPERATURE,
    actionField: 'maximum_channel_temperature',
    labelText: i18n.t('channels.settings.maximum_channel_temperature'),
    validationSchema: object({
      maximum_channel_temperature: number()
        .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
        .min(
          CHANNEL_SETTINGS_LIMITS.MAXIMUM_CHANNEL_TEMPERATURE.MIN,
          i18n.t('errors.numberValue.min', '', { minNum: CHANNEL_SETTINGS_LIMITS.MAXIMUM_CHANNEL_TEMPERATURE.MIN }),
        )
        .max(
          CHANNEL_SETTINGS_LIMITS.MAXIMUM_CHANNEL_TEMPERATURE.MAX,
          i18n.t('errors.numberValue.max', '', { maxNum: CHANNEL_SETTINGS_LIMITS.MAXIMUM_CHANNEL_TEMPERATURE.MAX }),
        )
        .required(i18n.t('errors.requiredValue')),
    }),
  },
  {
    inputType: 'number',
    actionName: ChangeChannelAction.SET_CHANNEL_TRIM_CHANNEL_SETPOINT,
    actionField: 'trim_channel_setpoint',
    labelText: i18n.t('channels.settings.trim_channel_setpoint'),
    validationSchema: object({
      trim_channel_setpoint: number()
        .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
        .min(
          CHANNEL_SETTINGS_LIMITS.TRIM_CHANNEL_SETPOINT.MIN,
          i18n.t('errors.numberValue.min', '', { minNum: CHANNEL_SETTINGS_LIMITS.TRIM_CHANNEL_SETPOINT.MIN }),
        )
        .max(
          CHANNEL_SETTINGS_LIMITS.TRIM_CHANNEL_SETPOINT.MAX,
          i18n.t('errors.numberValue.max', '', { maxNum: CHANNEL_SETTINGS_LIMITS.TRIM_CHANNEL_SETPOINT.MAX }),
        )
        .required(i18n.t('errors.requiredValue')),
    }),
  },
  {
    inputType: 'number',
    actionName: ChangeChannelAction.SET_CHANNEL_POWER_SCALE,
    actionField: 'power_output_scale',
    labelText: i18n.t('channels.settings.power_output_scale'),
    validationSchema: object({
      power_output_scale: number()
        .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
        .min(
          CHANNEL_SETTINGS_LIMITS.POWER_OUTPUT_SCALE.MIN,
          i18n.t('errors.numberValue.min', '', { minNum: CHANNEL_SETTINGS_LIMITS.POWER_OUTPUT_SCALE.MIN }),
        )
        .max(
          CHANNEL_SETTINGS_LIMITS.POWER_OUTPUT_SCALE.MAX,
          i18n.t('errors.numberValue.max', '', { maxNum: CHANNEL_SETTINGS_LIMITS.POWER_OUTPUT_SCALE.MAX }),
        )
        .required(i18n.t('errors.requiredValue')),
    }),
  },
];

export const channelDisableOutputAction: Omit<ChangeChannelActionType, 'inputType'> & { btnText: string } = {
  actionName: ChangeChannelAction.SET_CHANNEL_OUTPUT_CHANGE,
  labelText: 'Output',
  actionField: 'disabledOutput',
  btnText: 'Update',
};

export const channelTcButtonActions: (Omit<ChangeChannelActionType, 'inputType' | 'actionField'> & {
  action: ChannelTcAction;
})[] = [
  {
    actionName: ChangeChannelAction.SET_CHANNEL_TC_ASSIGN,
    labelText: 'Assign TC',
    action: ChannelTcAction.ASSIGN,
  },
  {
    actionName: ChangeChannelAction.SET_CHANNEL_TC_REMOVE,
    labelText: 'Remove TC',
    action: ChannelTcAction.REMOVE,
  },
];

export const channelButtonActions: Omit<ChangeChannelActionType, 'inputType' | 'actionField'>[] = [
  {
    actionName: ChangeChannelAction.CLEAR_CHANNEL_SETPOINT_ADJUST,
    labelText: 'Clear SP adjust',
  },
];

export const testChannelButtonAction: Omit<ChangeChannelActionType, 'inputType' | 'actionField'> = {
  actionName: ChangeChannelAction.TEST_CHANNEL,
  labelText: 'Test Channel',
};
